import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
	NOTIFICATION_EVENT_NAMES,
	type NotificationListResponse,
} from "@zplanem/responses"
import { useCallback } from "react"
import { apiFetch } from "../../lib/api-client"
import { usePerUserChannelEvents } from "./use-per-user-channel-event"

const notificationsQueryKey = ["me", "notifications"] as const

/**
 * The signed-in user's notification feed + live bell updates (ZPL-310).
 * `notification.created` on the per-user channel invalidates the feed — the
 * event carries the full DTO, but a refetch keeps unreadCount authoritative.
 */
export function useNotifications(userId: string | undefined) {
	const queryClient = useQueryClient()

	const feed = useQuery({
		queryKey: notificationsQueryKey,
		queryFn: () =>
			apiFetch<NotificationListResponse>("/api/common/users/me/notifications"),
		enabled: userId !== undefined,
		staleTime: 60_000,
	})

	const handler = useCallback(
		// The binding only delivers NOTIFICATION_EVENT_NAMES events (created,
		// read — ZPL-312/313 auto-read included); any of them stales the feed.
		() => {
			void queryClient.invalidateQueries({ queryKey: notificationsQueryKey })
		},
		[queryClient],
	)
	usePerUserChannelEvents(userId, NOTIFICATION_EVENT_NAMES, handler)

	const markRead = useMutation({
		mutationFn: (notificationId: string) =>
			apiFetch<void>(
				`/api/common/users/me/notifications/${notificationId}/read`,
				{ method: "POST" },
			),
		onSettled: () =>
			queryClient.invalidateQueries({ queryKey: notificationsQueryKey }),
	})

	const markAllRead = useMutation({
		mutationFn: () =>
			apiFetch<void>("/api/common/users/me/notifications/read-all", {
				method: "POST",
			}),
		onSettled: () =>
			queryClient.invalidateQueries({ queryKey: notificationsQueryKey }),
	})

	return { feed, markRead, markAllRead }
}
